import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, RouteObject, RouterProvider } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Toaster } from 'react-hot-toast';

import * as Sentry from '@sentry/react';
import { init as initFullStory } from '@fullstory/browser';
import { StytchB2BProvider } from '@stytch/react/b2b';
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b';

import 'unfonts.css';
import { LoginProvider, LoginRequired } from './auth';
import { Login, PasswordReset } from './components/StytchLogin';
import DashboardEmbed from './components/DashboardEmbed';
import NewTaskModal from './components/tasks/NewTaskModal';

import AppealReviewPage from './pages/AppealReviewPage';
import AppealReviewsTablePage from './pages/AppealReviewsTablePage';
import RxTablePage from './pages/RxTablePage';
import PriorAuthTablePage from './pages/PriorAuthTablePage';
import FaxVerificationPage from './pages/FaxVerificationPage';
import TutorialPage from './pages/HelpCenter/TutorialPage';
import HelpCenter from './pages/HelpCenter/HelpCenter';
import PendingEnrollmentPage from './pages/PendingEnrollmentPage';
import NotificationsTablePage from './pages/NotificationsTablePage';
import NewTaskPage from './pages/HelpCenter/NewTaskPage';
import ReferralPage from './pages/ReferralPage';
import PriorAuthReviewPage from './pages/PriorAuthReviewPage';
import PriorAuthReviewsTablePage from './pages/PriorAuthReviewsTablePage';
import RxPage from './pages/RxPage';
import SettingsPage from './pages/SettingsPage';
import SignupDocumentPage from './pages/LegacySignupDocumentPage';
import LegacySignupPage from './pages/LegacySignupPage';
import TaskPage from './pages/TaskPage';
import TremfyaExpressEnrollmentLoggedOutTaskPage from './pages/TremfyaExpressEnrollmentLoggedOutTaskPage';
import TasksTablePage from './pages/TasksTablePage';
import SignupFlow from './pages/Signup/SignupFlow';
import DemoFlow from './pages/Signup/DemoFlow';

Sentry.init({
  dsn: import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['partner.withtandem.com'],
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0, // Only capture sessions with errors - other sessions are in Fullstory already
  replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors
});

/**
 * FullStory -- collect sessions from production only
 */
if (import.meta.env.MODE === 'production') {
  initFullStory({ orgId: import.meta.env.VITE_FULLSTORY_ORG_ID });
}

const queryClient = new QueryClient();
queryClient.setDefaultOptions({
  queries: {
    refetchOnWindowFocus: false,
    refetchInterval: false,
  },
});

const stytch = new StytchB2BUIClient(import.meta.env.VITE_STYTCH_KEY);

const isDev = import.meta.env.MODE !== 'production';

const routes: RouteObject[] = [
  {
    path: '/demo',
    element: isDev ? <DemoFlow /> : <LegacySignupPage />,
  },
  {
    path: '/signup',
    element: isDev ? <SignupFlow /> : <LegacySignupPage />,
  },
  {
    path: '/tos-provider',
    element: <SignupDocumentPage />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/login/:slug',
    element: <Login />,
  },
  {
    path: '/password-reset',
    element: <PasswordReset />,
  },
  {
    path: '/analytics',
    element: (
      <LoginRequired>
        <DashboardEmbed />
      </LoginRequired>
    ),
  },
  {
    path: '/',
    element: <Navigate to="/prescriptions" replace />,
  },
  // Keeping both routes in case we want to explicitly link to /prescriptions
  {
    path: '/prescriptions',
    element: (
      <LoginRequired>
        <RxTablePage />
      </LoginRequired>
    ),
  },
  {
    path: '/tasks',
    element: (
      <LoginRequired>
        <TasksTablePage />
      </LoginRequired>
    ),
    children: [
      {
        path: 'new',
        element: <NewTaskModal />,
      },
    ],
  },
  {
    path: '/tasks/:taskId',
    element: (
      <LoginRequired>
        <TaskPage />
      </LoginRequired>
    ),
  },
  {
    path: '/tremfya-enrollment/:token',
    element: <TremfyaExpressEnrollmentLoggedOutTaskPage />,
  },
  {
    path: '/notifications',
    element: (
      <LoginRequired>
        <NotificationsTablePage />
      </LoginRequired>
    ),
  },
  {
    path: '/expiring-prior-auths',
    element: (
      <LoginRequired>
        <PriorAuthTablePage />
      </LoginRequired>
    ),
  },
  {
    path: '/appeal-reviews',
    element: (
      <LoginRequired>
        <AppealReviewsTablePage />
      </LoginRequired>
    ),
  },
  {
    path: '/appeal-reviews/:appealReviewId',
    element: (
      <LoginRequired>
        <AppealReviewPage />
      </LoginRequired>
    ),
  },
  {
    path: '/prior-auth-reviews',
    element: (
      <LoginRequired>
        <PriorAuthReviewsTablePage />
      </LoginRequired>
    ),
  },
  {
    path: '/prior-auth-reviews/:priorAuthReviewId',
    element: (
      <LoginRequired>
        <PriorAuthReviewPage />
      </LoginRequired>
    ),
  },
  {
    path: '/rxs/:rxId',
    element: (
      <LoginRequired>
        <RxPage />
      </LoginRequired>
    ),
    children: [
      {
        path: 'new-task',
        element: <NewTaskModal />,
      },
    ],
  },
  {
    path: '/settings',
    element: (
      <LoginRequired>
        <SettingsPage />
      </LoginRequired>
    ),
  },
  {
    path: '/help-center',
    element: (
      <LoginRequired>
        <HelpCenter />
      </LoginRequired>
    ),
    children: [
      {
        path: 'new-task',
        element: <NewTaskPage />,
      },
      {
        path: 'tutorial',
        element: <TutorialPage />,
      },
    ],
  },
  {
    path: '/referral',
    element: (
      <LoginRequired>
        <ReferralPage />
      </LoginRequired>
    ),
  },
  {
    path: '/verify-fax',
    element: (
      <LoginRequired>
        <FaxVerificationPage />
      </LoginRequired>
    ),
  },
  {
    path: '/enrollments/pending/:enrollmentId',
    element: (
      <LoginRequired>
        <PendingEnrollmentPage />
      </LoginRequired>
    ),
  },
];

const router = createBrowserRouter(routes);

function App() {
  return (
    <StytchB2BProvider stytch={stytch}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <LoginProvider>
            <RouterProvider router={router} />
            <Toaster position="bottom-right" toastOptions={{ duration: 3000 }} reverseOrder={true} />
          </LoginProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </StytchB2BProvider>
  );
}

export default App;
